import React, { useState } from "react";
import {
  Typography,
  Switch,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Box,
  Divider,
  MenuItem,
  Select,
  IconButton,
  useMediaQuery,
  useTheme,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import CloseIcon from "@mui/icons-material/Close";

import "./GeneralSetting.css"; // CSS file

// Days array
const days = Array.from({ length: 31 }, (_, i) => i + 1);
const reverseDays = [...days].reverse();

// Payroll Settings Data
const payrollSettings = [
  { label: "Pay Cycle", type: "dateRange" },
  { label: "Pay Frequency", value: "Monthly", type: "text" },
  { label: "Include Weekly Offs", type: "switch" },
  { label: "Include Holidays", type: "switch" },
  { label: "Fixed Pay Days", type: "switch" },
  { label: "Does Your Company Have PF?", type: "switch" },
  { label: "Employee Contribution", type: "switch" },
  { label: "PF Ceiling at Rs 15000", type: "switch" },
  { label: "Does Your Company Have ESI", type: "switch" },
  { label: "Do You Deduct Professional Tax", type: "switch" },
  { label: "Payroll Leave and Attendance Input Cycle", type: "dateRange" },
  { label: "Payout Date", value: "1", type: "number" },
  { label: "Payroll DOJ Cut Off Date For New Joinees", value: "31", type: "number" },
];

const GeneralSetting = () => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));

  const [editMode, setEditMode] = useState(false);
  const [switches, setSwitches] = useState({});
  const [formData, setFormData] = useState(() =>
    payrollSettings.reduce((acc, item) => {
      if (item.type === "dateRange") acc[item.label] = { from: "1", to: "31" };
      else acc[item.label] = item.value || "";
      return acc;
    }, {})
  );

  const handleToggle = (label) => {
    if (!editMode) return;
    setSwitches((prev) => ({ ...prev, [label]: !prev[label] }));
  };

  const handleInputChange = (label, value) => {
    setFormData((prev) => ({ ...prev, [label]: value }));
  };

  const handleDateChange = (label, field, value) => {
    setFormData((prev) => ({ ...prev, [label]: { ...prev[label], [field]: value } }));
  };

  const renderDateDropdown = (label) => {
    const value = formData[label];
    return (
      <Box className="gs-date-range">
        <Select
          size="small"
          disabled={!editMode}
          value={value.from}
          onChange={(e) => handleDateChange(label, "from", e.target.value)}
        >
          {days.map((d) => (
            <MenuItem key={d} value={d.toString()}>{d}</MenuItem>
          ))}
        </Select>
        <Typography className="gs-date-separator">to</Typography>
        <Select
          size="small"
          disabled={!editMode}
          value={value.to}
          onChange={(e) => handleDateChange(label, "to", e.target.value)}
        >
          {days.map((d) => (
            <MenuItem key={d} value={d.toString()}>{d}</MenuItem>
          ))}
        </Select>
      </Box>
    );
  };

  const renderNumberDropdown = (label, reverse = false) => {
    const options = reverse ? reverseDays : days;
    return (
      <Select
        size="small"
        disabled={!editMode}
        value={formData[label]}
        onChange={(e) => handleInputChange(label, e.target.value)}
      >
        {options.map((d) => (
          <MenuItem key={d} value={d.toString()}>{d}</MenuItem>
        ))}
      </Select>
    );
  };

  const handleSave = () => {
    console.log("Saved:", { ...formData, ...switches });
    setEditMode(false);
  };

  const handleCancel = () => setEditMode(false);

  return (
    <Box className="gs-wrapper">
      <Box className="gs-container" sx={{ maxWidth: isSmall ? "100%" : 1000 }}>
        <TableContainer component={Paper} className="gs-table-container">
          <Table stickyHeader size="small">
            <TableBody>
              <TableRow>
                <TableCell colSpan={2}>
                  <Box className="gs-table-header">
                    <Typography variant="h6" fontWeight="bold">
                      Payroll Settings
                    </Typography>
                    <Box className="gs-action-buttons">
                      <Typography fontWeight="bold">Action</Typography>
                      <IconButton
                        color="primary"
                        onClick={() => setEditMode(true)}
                        disabled={editMode}
                      >
                        <EditIcon />
                      </IconButton>
                    </Box>
                  </Box>
                </TableCell>
              </TableRow>

              {payrollSettings.map((item, index) => (
                <TableRow
                  key={index}
                  className={index % 2 === 0 ? "gs-row-even" : "gs-row-odd"}
                >
                  <TableCell className="gs-label">{item.label}</TableCell>
                  <TableCell>
                    {item.type === "switch" ? (
                      <Switch
                        checked={!!switches[item.label]}
                        onChange={() => handleToggle(item.label)}
                        disabled={!editMode}
                      />
                    ) : item.type === "dateRange" ? (
                      renderDateDropdown(item.label)
                    ) : item.label === "Payout Date" ? (
                      renderNumberDropdown(item.label)
                    ) : item.label === "Payroll DOJ Cut Off Date For New Joinees" ? (
                      renderNumberDropdown(item.label, true)
                    ) : editMode ? (
                      <TextField
                        size="small"
                        value={formData[item.label]}
                        onChange={(e) => handleInputChange(item.label, e.target.value)}
                        fullWidth
                      />
                    ) : (
                      <Typography>{formData[item.label]}</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}

              {editMode && (
                <>
                  <TableRow>
                    <TableCell colSpan={2}>
                      <Divider sx={{ my: 1 }} />
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell colSpan={2} align="right">
                      <Box className="gs-save-cancel">
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={handleCancel}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant="contained"
                          color="success"
                          startIcon={<SaveIcon />}
                          onClick={handleSave}
                        >
                          Save
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default GeneralSetting;
